import { combineReducers } from '@reduxjs/toolkit';
import {
    ConfigurationParameters as AchatsConfigurationParameters,
    Configuration as AchatsConfiguration,
    MediaObjectApi,
    FactureAchatApi,
    CodeRejetApi,
    ExceptionFacturanteApi,
    DefFrequenceApi,
    ReminderPublisherApi,
    ActionApi,
    AttachmentApi,
    CategorieRejetApi,
    ExceptionRejetApi,
    FactureAchatCodeRejetApi,
    FactureAchatJalApi,
    FactureAchatLigneApi,
    FactureAchatNoteApi,
    MediaObjectNoteApi,
    ReleveAchatApi,
    WorkflowMailApi,
} from '../../openApi/ApiAchats';
import {
    createMediaObjectSlice,
    MEDIAOBJECT_SLICE_NAME,
    UNASSIGNED_UNREGISTERED_INVOICES_DATASOURCE_NAME,
    UNREGISTERED_INVOICES_DATASOURCE_NAME,
    USER_UNREGISTERED_INVOICES_DATASOURCE_NAME,
} from './slices/mediaObjectsSlice';
import {
    createFactureAchatSlice,
    FACTURES_ACHATS_FROM_PUBLISHER_REMINDER_DATASOURCE_NAME,
    FACTURES_ACHATS_FROM_STATEMENT_DATASOURCE_NAME,
    FACTURES_ACHATS_PAUSE_DATASOURCE_NAME,
    FACTURES_ACHATS_SLICE_NAME,
    FACTURES_ACHATS_STATS_DATASOURCE_NAME,
} from './slices/factureAchatSlice';
import {
    CATEGORIE_STATS_SLICE_NAME,
    createCategorieStatsSlice,
} from './slices/categorieStatsSlice';
import {
    CODE_REJET_STATS_SLICE_NAME,
    createCodeRejetStatsSlice,
} from './slices/codeRejetStatsSlice';
import {
    createReleveAchatSlice,
    INVOICE_STATEMENT_SLICE_NAME,
} from './slices/invoiceStatementSlice';
import {
    CODES_REJETS_FOURNISSEUR_DATASOURCE_NAME,
    CODES_REJETS_SLICE_NAME,
    createCodeRejetSlice,
} from './slices/codeRejetSlice';
import {
    createExceptionFacturanteSlice,
    EXCEPTIONS_FACTURANTES_SLICE_NAME,
} from './slices/exceptionFacturante';
import { createDefFrequenceSlice, DEF_FREQUENCE_SLICE_NAME } from './slices/defFrequenceSlice';
import {
    createReminderPublisherSlice,
    REMINDER_PUBLISHER_SLICE_NAME,
} from './slices/reminderPublisherSlice';

export { type AchatsConfigurationParameters };

export function createMediaobjectReducer(configuration: AchatsConfigurationParameters) {
    const { mediaobjectSlice, mediaobjectDataSourcesThunks } =
        createMediaObjectSlice(configuration);

    return { mediaobjectSlice, mediaobjectDataSourcesThunks };
}

export function createReleveAchatReducer(configuration: AchatsConfigurationParameters) {
    const { invoiceStatementSlice, invoiceStatementDataSourcesThunks } =
        createReleveAchatSlice(configuration);

    return { invoiceStatementSlice, invoiceStatementDataSourcesThunks };
}

export function createFactureAchatReducer(configuration: AchatsConfigurationParameters) {
    const { factureAchatSlice, factureAchatDataSourcesThunks } =
        createFactureAchatSlice(configuration);

    return { factureAchatSlice, factureAchatDataSourcesThunks };
}

export function createCategorieStatsReducer(configuration: AchatsConfigurationParameters) {
    const { categorieStatsSlice, categorieStatsDataSourcesThunks } =
        createCategorieStatsSlice(configuration);

    return { categorieStatsSlice, categorieStatsDataSourcesThunks };
}

export function createCodeRejetStatsReducer(configuration: AchatsConfigurationParameters) {
    const { codeRejetStatsSlice, codeRejetStatsDataSourcesThunks } =
        createCodeRejetStatsSlice(configuration);

    return { codeRejetStatsSlice, codeRejetStatsDataSourcesThunks };
}

export function createCodeRejetReducer(configuration: AchatsConfigurationParameters) {
    const { codeRejetSlice, codeRejetDataSourcesThunks } = createCodeRejetSlice(configuration);

    return { codeRejetSlice, codeRejetDataSourcesThunks };
}

export function createReminderPublisherReducer(configuration: AchatsConfigurationParameters) {
    const { reminderPublisherSlice, reminderPublisherDataSourcesThunks } =
        createReminderPublisherSlice(configuration);

    return { reminderPublisherSlice, reminderPublisherDataSourcesThunks };
}

export function createExceptionFacturanteReducer(configuration: AchatsConfigurationParameters) {
    const { exceptionFacturanteSlice, exceptionFacturanteDataSourcesThunks } =
        createExceptionFacturanteSlice(configuration);

    return { exceptionFacturanteSlice, exceptionFacturanteDataSourcesThunks };
}

export function createDefFrequenceReducer(configuration: AchatsConfigurationParameters) {
    const { defFrequenceSlice, defFrequenceDataSourcesThunks } =
        createDefFrequenceSlice(configuration);

    return { defFrequenceSlice, defFrequenceDataSourcesThunks };
}

export function createAchatsReducers(configuration: AchatsConfigurationParameters) {
    return {
        ...createMediaobjectReducer(configuration),
        ...createReleveAchatReducer(configuration),
        ...createFactureAchatReducer(configuration),
        ...createCategorieStatsReducer(configuration),
        ...createCodeRejetStatsReducer(configuration),
        ...createCodeRejetReducer(configuration),
        ...createReminderPublisherReducer(configuration),
        ...createExceptionFacturanteReducer(configuration),
        ...createDefFrequenceReducer(configuration),
    };
}

export function buildAchatsRedux(configparams: AchatsConfigurationParameters) {
    const achatsConfiguration = new AchatsConfiguration(configparams);

    const actionApi = new ActionApi(achatsConfiguration);
    const attachmentApi = new AttachmentApi(achatsConfiguration);
    const categorieRejetApi = new CategorieRejetApi(achatsConfiguration);
    const codeRejetApi = new CodeRejetApi(achatsConfiguration);
    const defFrequenceApi = new DefFrequenceApi(achatsConfiguration);
    const exceptionFacturanteApi = new ExceptionFacturanteApi(achatsConfiguration);
    const exceptionRejetApi = new ExceptionRejetApi(achatsConfiguration);
    const factureAchatApi = new FactureAchatApi(achatsConfiguration);
    const factureAchatCodeRejetApi = new FactureAchatCodeRejetApi(achatsConfiguration);
    const factureAchatJalApi = new FactureAchatJalApi(achatsConfiguration);
    const factureAchatLigneApi = new FactureAchatLigneApi(achatsConfiguration);
    const factureAchatNoteApi = new FactureAchatNoteApi(achatsConfiguration);
    const mediaObjectApi = new MediaObjectApi(achatsConfiguration);
    const mediaObjectNoteApi = new MediaObjectNoteApi(achatsConfiguration);
    const releveAchatApi = new ReleveAchatApi(achatsConfiguration);
    const reminderPublisherApi = new ReminderPublisherApi(achatsConfiguration);
    const workflowMailApi = new WorkflowMailApi(achatsConfiguration);

    const {
        categorieStatsDataSourcesThunks,
        categorieStatsSlice,
        codeRejetDataSourcesThunks,
        codeRejetSlice,
        codeRejetStatsDataSourcesThunks,
        codeRejetStatsSlice,
        defFrequenceDataSourcesThunks,
        defFrequenceSlice,
        exceptionFacturanteDataSourcesThunks,
        exceptionFacturanteSlice,
        factureAchatDataSourcesThunks,
        factureAchatSlice,
        invoiceStatementDataSourcesThunks,
        invoiceStatementSlice,
        mediaobjectDataSourcesThunks,
        mediaobjectSlice,
        reminderPublisherDataSourcesThunks,
        reminderPublisherSlice,
    } = createAchatsReducers(achatsConfiguration);

    const combinedAchatReducers = combineReducers({
        [CATEGORIE_STATS_SLICE_NAME]: categorieStatsSlice.reducer,
        [CODES_REJETS_SLICE_NAME]: codeRejetSlice.reducer,
        [CODE_REJET_STATS_SLICE_NAME]: codeRejetStatsSlice.reducer,
        [DEF_FREQUENCE_SLICE_NAME]: defFrequenceSlice.reducer,
        [EXCEPTIONS_FACTURANTES_SLICE_NAME]: exceptionFacturanteSlice.reducer,
        [FACTURES_ACHATS_SLICE_NAME]: factureAchatSlice.reducer,
        [INVOICE_STATEMENT_SLICE_NAME]: invoiceStatementSlice.reducer,
        [MEDIAOBJECT_SLICE_NAME]: mediaobjectSlice.reducer,
        [REMINDER_PUBLISHER_SLICE_NAME]: reminderPublisherSlice.reducer,
    });

    return {
        serviceConfiguration: achatsConfiguration,
        serviceConfigurationParameters: configparams,
        reducer: combinedAchatReducers,
        apis: {
            actionApi,
            attachmentApi,
            categorieRejetApi,
            codeRejetApi,
            defFrequenceApi,
            exceptionFacturanteApi,
            exceptionRejetApi,
            factureAchatApi,
            factureAchatCodeRejetApi,
            factureAchatJalApi,
            factureAchatLigneApi,
            factureAchatNoteApi,
            mediaObjectApi,
            mediaObjectNoteApi,
            releveAchatApi,
            reminderPublisherApi,
            workflowMailApi,
        },
        slices: {
            categorieStatsSlice,
            codeRejetSlice,
            codeRejetStatsSlice,
            defFrequenceSlice,
            exceptionFacturanteSlice,
            factureAchatSlice,
            invoiceStatementSlice,
            mediaobjectSlice,
            reminderPublisherSlice,
        },
        thunks: {
            categorieStatsDataSourcesThunks,
            codeRejetDataSourcesThunks,
            codeRejetStatsDataSourcesThunks,
            defFrequenceDataSourcesThunks,
            exceptionFacturanteDataSourcesThunks,
            factureAchatDataSourcesThunks,
            invoiceStatementDataSourcesThunks,
            mediaobjectDataSourcesThunks,
            reminderPublisherDataSourcesThunks,
        },
        fetchItems: {
            getCategorieStats: categorieStatsDataSourcesThunks.main.getData,
            getCodeRejet: codeRejetDataSourcesThunks.main.getData,
            getCodeRejetStats: codeRejetStatsDataSourcesThunks.main.getData,
            getDefFrequence: defFrequenceDataSourcesThunks.main.getData,
            getExceptionFacturante: exceptionFacturanteDataSourcesThunks.main.getData,
            getFactureAchat: factureAchatDataSourcesThunks.main.getData,
            getInvoiceStatement: invoiceStatementDataSourcesThunks.main.getData,
            getMediaobject: mediaobjectDataSourcesThunks.main.getData,
            getUnregisteredMediaobject:
                mediaobjectDataSourcesThunks[UNREGISTERED_INVOICES_DATASOURCE_NAME].getData,
            getUserUnregisteredMediaobject:
                mediaobjectDataSourcesThunks[USER_UNREGISTERED_INVOICES_DATASOURCE_NAME].getData,
            getUnassignedMediaobject:
                mediaobjectDataSourcesThunks[UNASSIGNED_UNREGISTERED_INVOICES_DATASOURCE_NAME]
                    .getData,
            getReminderPublisher: reminderPublisherDataSourcesThunks.main.getData,
            getCodeRejetFournisseur:
                codeRejetDataSourcesThunks[CODES_REJETS_FOURNISSEUR_DATASOURCE_NAME].getData,
            getFactureAchatFromReminderPublisher:
                factureAchatDataSourcesThunks[
                    FACTURES_ACHATS_FROM_PUBLISHER_REMINDER_DATASOURCE_NAME
                ].getData,
            getFactureAchatFromStatement:
                factureAchatDataSourcesThunks[FACTURES_ACHATS_FROM_STATEMENT_DATASOURCE_NAME]
                    .getData,
            getFactureAchatPause:
                factureAchatDataSourcesThunks[FACTURES_ACHATS_PAUSE_DATASOURCE_NAME].getData,
            getFactureAchatStats:
                factureAchatDataSourcesThunks[FACTURES_ACHATS_STATS_DATASOURCE_NAME].getData,
        },
        getItems: {
            selectCategorieStats: categorieStatsDataSourcesThunks.main.getSelected,
            selectCodeRejet: codeRejetDataSourcesThunks.main.getSelected,
            selectCodeRejetStats: codeRejetStatsDataSourcesThunks.main.getSelected,
            selectDefFrequence: defFrequenceDataSourcesThunks.main.getSelected,
            selectExceptionFacturante: exceptionFacturanteDataSourcesThunks.main.getSelected,
            selectFactureAchat: factureAchatDataSourcesThunks.main.getSelected,
            selectInvoiceStatement: invoiceStatementDataSourcesThunks.main.getSelected,
            selectMediaobject: mediaobjectDataSourcesThunks.main.getSelected,
            selectReminderPublisher: reminderPublisherDataSourcesThunks.main.getSelected,
            selectCodeRejetFournisseur:
                codeRejetDataSourcesThunks[CODES_REJETS_FOURNISSEUR_DATASOURCE_NAME].getSelected,
            selectFactureAchatFromReminderPublisher:
                factureAchatDataSourcesThunks[
                    FACTURES_ACHATS_FROM_PUBLISHER_REMINDER_DATASOURCE_NAME
                ].getSelected,
            selectFactureAchatFromStatement:
                factureAchatDataSourcesThunks[FACTURES_ACHATS_FROM_STATEMENT_DATASOURCE_NAME]
                    .getSelected,
            selectFactureAchatPause:
                factureAchatDataSourcesThunks[FACTURES_ACHATS_PAUSE_DATASOURCE_NAME].getSelected,
            selectFactureAchatStats:
                factureAchatDataSourcesThunks[FACTURES_ACHATS_STATS_DATASOURCE_NAME].getSelected,
        },
        models: {},
    };
}
